// <booking-form> - the tattoo booking modal: 3 steps, conditional field
// visibility driven by [data-show-if], a body-part/detail-level session
// estimator, sessionStorage persistence (so a trip to the portfolio link
// and back doesn't lose progress), and a POST to the booking-form-worker
// Cloudflare Worker on submit. See business/products/tattoo-booking-form.md
// and the implementation plan for the field-by-field spec this follows.

const STORAGE_KEY = "tattooBookingFormState";

// This file is never Liquid-processed, so it can't translate anything on its
// own - the section renders every UI string this file needs into a JSON
// blob (data-tbf-i18n) already in the storefront's language, and this just
// reads it at runtime. tformat() fills in the {placeholder} tokens some of
// those strings carry (deliberately plain single braces, not Liquid's own
// {{ }} syntax, so nothing here gets mistaken for a Liquid tag).
// The JSON blob's strings went through Liquid's `t` filter, which
// HTML-escapes by default (turns "I'll" into "I&#39;ll") - harmless for
// normal Liquid output since the browser's HTML parser decodes entities
// back automatically wherever that text lands on the page, but this JSON
// lives inside a <script> tag, which is never parsed as HTML - so it needs
// decoding by hand here instead, or every apostrophe/quote in a translated
// string would show up literally as &#39;/&quot; on screen.
function tbfDecodeEntities(value) {
  if (typeof value === "string") {
    const el = document.createElement("textarea");
    el.innerHTML = value;
    return el.value;
  }
  if (Array.isArray(value)) return value.map(tbfDecodeEntities);
  if (value && typeof value === "object") {
    const out = {};
    for (const k in value) out[k] = tbfDecodeEntities(value[k]);
    return out;
  }
  return value;
}
function tbfI18n() {
  const el = document.querySelector("[data-tbf-i18n]");
  if (el) {
    try {
      return tbfDecodeEntities(JSON.parse(el.textContent));
    } catch (e) {
      // fall through to the English fallback below
    }
  }
  return {
    stepLabel: "Step {current} of {total}",
    validationGeneric: "Please fill in the highlighted field.",
    validationQuestion: 'Please answer: "{question}"',
    zoneRequired: "Pick at least one area",
    selectedCount: "{count} selected",
    filesNone: "No files selected",
    filesOne: "1 file selected",
    filesMany: "{count} files selected",
    months: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
    calendarPrev: "<",
    calendarNext: ">",
    calendarDone: "Done",
    weekdays: ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"],
    estimatePlaceholder: "Pick your area(s) to see an estimate.",
    noCustomArea: "No problem — I'll take a look at your description below.",
    estimateLabel: "Estimated sessions: {range}",
    sessionSingular: "session",
    sessionPlural: "sessions",
    roundingNote: "Estimates per area are rounded separately and may not add up exactly to the total above.",
    notePain: "The exact number also depends on your pain tolerance and body size — we'll confirm during our chat.",
    noteStepByStep: "You don't have to book every session at once — we can take it step by step, at your pace.",
    notePartialCoverage: "Only partly covering a picked area? Halve that area's number — the estimate assumes full coverage.",
    statusSending: "Sending...",
    statusErrorGeneric: "Something went wrong",
    statusSuccess: "Thanks! Your request is in - I'll get back to you soon.",
    statusErrorPrefix: "Couldn't send that: {message}",
  };
}
const TBF_I18N = tbfI18n();
function tformat(str, vars) {
  return str.replace(/\{(\w+)\}/g, (m, key) => (vars && key in vars ? vars[key] : m));
}

class BookingForm extends HTMLElement {
  constructor() {
    super();
    this.currentStep = 1;
    this.totalSteps = 3;
  }

  connectedCallback() {
    this.dialogEl = this.querySelector("[data-booking-dialog]");
    this.dialogInner = this.querySelector(".booking-form__dialog-inner");
    this.formEl = this.querySelector("form");
    this.openButtons = document.querySelectorAll("[data-booking-open]");
    this.closeButtons = this.querySelectorAll("[data-booking-close]");
    this.nextButtons = this.querySelectorAll("[data-booking-next]");
    this.backButtons = this.querySelectorAll("[data-booking-back]");
    this.stepEls = this.querySelectorAll("[data-step]");
    this.stepLabel = this.querySelector("[data-step-label]");
    this.statusEl = this.querySelector("[data-booking-status]");
    this.submitButton = this.querySelector("[data-booking-submit]");
    this.portfolioLink = this.querySelector("[data-booking-portfolio-link]");

    this.openButtons.forEach((btn) => btn.addEventListener("click", () => this.open()));
    this.closeButtons.forEach((btn) => btn.addEventListener("click", () => this.close()));
    this.nextButtons.forEach((btn) => btn.addEventListener("click", () => this.goNext()));
    this.backButtons.forEach((btn) => btn.addEventListener("click", () => this.goBack()));
    this.formEl.addEventListener("submit", (e) => this.handleSubmit(e));
    // Pressing Enter in a text field submits the form via the hidden
    // Step-3 submit button (the browser's default-button behavior picks the
    // first type="submit" button in the form regardless of it being
    // `hidden`) - that fired a real POST to the worker while still on step
    // 1 or 2, which the worker correctly rejected for missing later-step
    // fields, surfacing as a confusing "Couldn't send that" error on Next.
    // Route Enter through the same step navigation Next uses instead.
    this.formEl.addEventListener("keydown", (e) => {
      if (e.key !== "Enter" || e.target.tagName === "TEXTAREA") return;
      e.preventDefault();
      if (this.currentStep < this.totalSteps) this.goNext();
      else this.formEl.requestSubmit();
    });
    this.formEl.addEventListener("input", () => this.persistState());
    this.formEl.addEventListener("change", (e) => {
      this.updateVisibility();
      this.updateSessionEstimate();
      this.updateDetailPreview();
      this.updateStylePreview();
      this.updateZonePreview();
      this.updateFloatingSummary();
      this.persistState();
    });
    if (this.portfolioLink) {
      this.portfolioLink.addEventListener("click", () => {
        this.persistState();
        this.close({ keepState: true });
      });
    }
    // By request: the X button is the only way out. No backdrop click, no
    // Escape key - both were closing the form mid-interaction and losing
    // the visitor's place (compounded by the reflow-triggered false
    // backdrop-click bug fixed earlier). `cancel` fires on Escape;
    // preventDefault stops the native close.
    this.dialogEl.addEventListener("cancel", (e) => e.preventDefault());

    // Suppress the browser's own validation bubble on every field - it's
    // unstyleable OS/browser chrome (wrong font, wrong language, doesn't
    // match the site). `capture: true` so this runs before the browser
    // decides whether to show it. Our own message (see reportInvalidField)
    // replaces it.
    this.formEl.querySelectorAll("input, select, textarea").forEach((field) => {
      field.addEventListener("invalid", (e) => e.preventDefault());
    });

    this.setupAutoGrowTextarea();
    this.setupZoneGroups();
    this.setupFilePickers();
    this.setupAutocomplete();
    this.setupDatePickers();
    this.setupFloatingEstimate();
    this.setupFooterDocking();
    this.restoreState();
    this.updateVisibility();
    this.updateSessionEstimate();
    this.updateDetailPreview();
    this.updateStylePreview();
    this.updateZonePreview();
    this.updateFloatingSummary();
    this.renderStep();
  }

  // ---------------------------------------------------------------------
  // Open / close
  // ---------------------------------------------------------------------

  open() {
    if (typeof this.dialogEl.showModal === "function") {
      this.dialogEl.showModal();
    } else {
      this.dialogEl.setAttribute("open", "");
    }
    document.body.classList.add("booking-form-open");
    this.setStatus("", "");
    this.renderStep();
    // Only measurable once actually visible - offsetHeight reads 0 on a
    // closed <dialog>, which is why this can't just run once at
    // connectedCallback time the way most other setup here does.
    this.syncHeaderHeightVar();
  }

  // The zone-preview diagram sticks right below the header (see .zone-preview
  // in the CSS), but the header's real rendered height isn't a fixed
  // constant - it depends on the theme's font-size settings and the current
  // step's label text - so it's measured here instead of hardcoded, and
  // written out as a CSS custom property the sticky rule reads.
  syncHeaderHeightVar() {
    const header = this.querySelector(".booking-form__header");
    if (!header || !this.dialogEl) return;
    this.dialogEl.style.setProperty("--bform-header-height", `${header.offsetHeight}px`);
  }

  close({ keepState } = {}) {
    if (typeof this.dialogEl.close === "function") {
      this.dialogEl.close();
    } else {
      this.dialogEl.removeAttribute("open");
    }
    document.body.classList.remove("booking-form-open");
    if (!keepState) {
      // Leaving the flow without an intent to come straight back (e.g. just
      // dismissing) - state stays in sessionStorage regardless, cheap and
      // harmless, so a visitor who reopens later still resumes.
    }
  }

  // ---------------------------------------------------------------------
  // Step navigation
  // ---------------------------------------------------------------------

  renderStep() {
    this.stepEls.forEach((el) => {
      el.classList.toggle("is-active", Number(el.dataset.step) === this.currentStep);
    });
    if (this.stepLabel) this.stepLabel.textContent = tformat(TBF_I18N.stepLabel, { current: this.currentStep, total: this.totalSteps });
    this.backButtons.forEach((btn) => btn.toggleAttribute("hidden", this.currentStep === 1));
    this.nextButtons.forEach((btn) => btn.toggleAttribute("hidden", this.currentStep === this.totalSteps));
    if (this.submitButton) this.submitButton.toggleAttribute("hidden", this.currentStep !== this.totalSteps);
    // Switching steps swaps which .booking-form__step is display:flex, but
    // the scroll container's scrollTop doesn't reset on its own - without
    // this, landing on step 2 while still scrolled to where step 1 ended
    // (e.g. bottom of a long step 1) leaves the visitor looking at the
    // bottom of step 2 instead of its first question.
    if (this.dialogInner) this.dialogInner.scrollTop = 0;
    // The floating estimate bar only makes sense while step 2 (the
    // constructor) is on screen - re-evaluate immediately on navigation
    // rather than waiting for the IntersectionObserver to catch up.
    this.applyFloatingEstimateVisibility();
    this.persistState();
  }

  currentStepFields() {
    const stepEl = this.querySelector(`[data-step="${this.currentStep}"]`);
    return Array.from(stepEl.querySelectorAll("input, select, textarea")).filter((el) => this.isVisible(el));
  }

  isVisible(el) {
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
  }

  validateCurrentStep() {
    const fields = this.currentStepFields();
    let firstInvalid = null;
    let valid = true;

    // project_types ("Any special circumstances?") is optional - leaving it
    // blank means "just a new tattoo", so unlike the zone constructor below
    // it has no at-least-one-checked requirement.

    // Body-zone constructor: at least one sub-part checked. Guarded by a
    // zone-group toggle button's visibility (always in normal flow on step
    // 2, unlike the sub-part checkboxes themselves, which can sit inside a
    // currently-collapsed panel and read as invisible).
    const zoneGroupAnchor = this.querySelector("[data-zone-group-toggle]");
    if (zoneGroupAnchor && this.isVisible(zoneGroupAnchor)) {
      const zoneBoxes = this.querySelectorAll("input[data-zone-part]");
      const anyChecked = Array.from(zoneBoxes).some((b) => b.checked);
      zoneBoxes[0]?.setCustomValidity(anyChecked ? "" : TBF_I18N.zoneRequired);
    }

    // DOM order (currentStepFields() is built via querySelectorAll, which
    // already returns elements in document order) decides which invalid
    // field is reported first - including the two custom checks above,
    // since their target checkboxes are themselves part of `fields`.
    for (const field of fields) {
      if (!field.checkValidity()) {
        valid = false;
        firstInvalid = firstInvalid || field;
      }
    }

    if (!valid && firstInvalid) {
      this.reportInvalidField(firstInvalid);
    } else {
      this.setStatus("", "");
    }
    return valid;
  }

  // The browser's native validation bubble ("Please select one of these
  // options" etc.) can't be styled or translated - it's OS/browser chrome,
  // not part of the page. `invalid` listeners (registered once, below)
  // suppress it everywhere; this shows our own message in the status line
  // instead and scrolls the relevant question into view.
  reportInvalidField(field) {
    // A zone-part checkbox can sit inside a currently-collapsed group panel
    // - reveal it first, otherwise scrollIntoView() on a display:none
    // element does nothing and the visitor can't see what to fix.
    const collapsedParts = field.closest("[data-zone-group-parts].is-collapsed");
    if (collapsedParts) {
      collapsedParts.classList.remove("is-collapsed");
      collapsedParts.closest("[data-zone-group]")?.classList.add("is-open");
    }
    const question = field.closest(".bform-field")?.querySelector(".bform-label")?.textContent?.trim();
    this.setStatus(question ? tformat(TBF_I18N.validationQuestion, { question }) : TBF_I18N.validationGeneric, "error");
    field.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  goNext() {
    if (!this.validateCurrentStep()) return;
    this.currentStep = Math.min(this.totalSteps, this.currentStep + 1);
    this.renderStep();
  }

  goBack() {
    this.currentStep = Math.max(1, this.currentStep - 1);
    this.renderStep();
  }

  // ---------------------------------------------------------------------
  // Conditional visibility - every [data-show-if="name:value"] element is
  // shown only when the named field currently has that value (radio/select),
  // or is checked (checkbox, value omitted). Fields inside get `required`
  // toggled to match, so hidden branches never block submission.
  // ---------------------------------------------------------------------

  updateVisibility() {
    this.querySelectorAll("[data-show-if]").forEach((el) => {
      const [name, expected] = el.dataset.showIf.split(":");
      const show = this.fieldMatches(name, expected);
      el.classList.toggle("is-hidden", !show);
      el.querySelectorAll("[data-required-when-visible]").forEach((input) => {
        input.required = show;
        if (!show) input.setCustomValidity("");
      });
      // A hidden branch's own inputs must not be submitted/considered even
      // if they don't carry data-required-when-visible.
      el.querySelectorAll("input, select, textarea").forEach((input) => {
        input.disabled = !show && input.dataset.keepEnabled !== "true";
      });
    });
  }

  fieldMatches(name, expected) {
    const els = this.querySelectorAll(`[name="${name}"]`);
    if (!els.length) return false;
    if (els[0].type === "checkbox" && expected === undefined) {
      return els[0].checked;
    }
    const checked = Array.from(els).find((el) => (el.type === "radio" || el.type === "checkbox" ? el.checked : true));
    if (!checked) return false;
    return String(checked.value) === expected;
  }

  // ---------------------------------------------------------------------
  // Body-area constructor: each big zone (Arm/Leg/...) is a collapsible
  // group; clicking its header only expands/collapses its own sub-part
  // list (shoulder/bicep/elbow/...), it never selects anything by itself.
  // Collapsing never clears what's checked inside - a visitor needs to
  // open Leg's list without losing what they picked in Arm's. Each
  // sub-part is a fully independent checkbox; a paired one (left/right,
  // part/whole) carries its own compact switch, not shared with any other
  // sub-part - so a visitor can pick a full sleeve on one arm and just the
  // bicep on the other without anything cascading between them. (An
  // earlier "whole X" select-all shortcut per group was removed - it added
  // real interaction bugs for one extra click saved.)
  // ---------------------------------------------------------------------

  // The idea-description textarea grows downward on its own as the
  // visitor types, instead of offering a manual drag-to-resize handle
  // (which also let it stretch sideways, not just taller - see the CSS
  // `resize: none` on textarea.bform-input).
  setupAutoGrowTextarea() {
    // The starting floor (~4 rows) is set in CSS (min-height on
    // textarea.bform-input), not measured here - this runs at page load,
    // while the dialog can still be closed/hidden, and a layout read
    // (offsetHeight) on a hidden element is always 0. CSS min-height still
    // clamps the box from below even once JS sets an explicit smaller
    // inline height, so it doesn't have that problem.
    this.querySelectorAll("textarea.bform-input").forEach((el) => {
      const grow = () => {
        el.style.height = "auto";
        el.style.height = `${el.scrollHeight}px`;
      };
      el.addEventListener("input", grow);
      grow();
    });
  }

  setupZoneGroups() {
    this.querySelectorAll("[data-zone-group]").forEach((group) => {
      const toggleBtn = group.querySelector("[data-zone-group-toggle]");
      const parts = group.querySelector("[data-zone-group-parts]");
      if (!toggleBtn || !parts) return;

      toggleBtn.addEventListener("click", () => {
        const collapsed = parts.classList.toggle("is-collapsed");
        group.classList.toggle("is-open", !collapsed);
      });

      group.querySelectorAll("input[data-zone-part]").forEach((box) => {
        box.addEventListener("change", () => this.syncZoneGroupBadge(group));
      });

      this.syncZoneGroupBadge(group);
    });

    // Each sub-part row pairs an option-card (the checkbox itself) with an
    // independent switch beside it, not nested inside it - two separate
    // controls a visitor can't miss-tap on a phone. Click anywhere on the
    // card flips the checkbox; the switch handles its own clicks natively.
    this.querySelectorAll("[data-zone-part-row]").forEach((row) => {
      const card = row.querySelector("[data-zone-part-card]");
      const checkbox = card?.querySelector("input[data-zone-part]");
      const toggle = row.querySelector("[data-zone-part-toggle]");
      const toggleInput = toggle?.querySelector("input");
      if (!card || !checkbox) return;

      const sync = () => {
        if (!toggle) return;
        // visibility, not display:none/.is-hidden - a display:none toggle
        // drops out of the shared CSS Grid entirely, which was the actual
        // cause of rows misaligning/reshuffling as boxes got checked (see
        // the CSS comment on .zone-part__toggle for the full story).
        toggle.classList.toggle("is-visible", checkbox.checked);
        // Once this part is unchecked, reset its switch to the default
        // ("one") so re-checking it later starts unanswered again instead
        // of silently resuming whatever was picked before.
        if (!checkbox.checked && toggleInput) toggleInput.checked = false;
      };
      checkbox.addEventListener("change", sync);
      if (toggleInput) toggleInput.addEventListener("change", () => this.updateZonePreview());
      card.addEventListener("click", () => {
        if (checkbox.disabled) return;
        checkbox.checked = !checkbox.checked;
        checkbox.dispatchEvent(new Event("change", { bubbles: true }));
      });
      sync();
    });

    this.setupCustomAreaExclusivity();
  }

  // "I don't see the area I want" and the zone constructor are mutually
  // exclusive - picking one turns the other off, so a submitted request (and
  // the notification email) never ends up showing both a specific picked
  // area *and* "area isn't in the list" at once, which read as
  // contradictory. Checking the custom-area box clears and disables every
  // zone-part checkbox; checking any zone-part unchecks the custom-area box.
  setupCustomAreaExclusivity() {
    const customCheckbox = this.querySelector("input[data-zone-custom]");
    const zoneGroupsEl = this.querySelector(".zone-groups");
    if (!customCheckbox || !zoneGroupsEl) return;

    const partBoxes = () => Array.from(this.querySelectorAll("input[data-zone-part]"));

    // Fully hides the constructor rather than just greying it out, and
    // deliberately does NOT clear checked parts when hiding - disabling
    // already excludes them from submission (see checkedParts guards in
    // updateSessionEstimate/updateZonePreview/handleSubmit), and keeping
    // the checkboxes checked means unchecking "not listed" later brings
    // the previous picks straight back instead of making the visitor
    // re-select everything.
    const applyCustomState = () => {
      const on = customCheckbox.checked;
      zoneGroupsEl.classList.toggle("is-hidden", on);
      partBoxes().forEach((box) => {
        box.disabled = on;
      });
      this.updateSessionEstimate();
      this.updateZonePreview();
    };

    customCheckbox.addEventListener("change", applyCustomState);
    partBoxes().forEach((box) => {
      box.addEventListener("change", () => {
        if (box.checked && customCheckbox.checked) {
          customCheckbox.checked = false;
          applyCustomState();
        }
      });
    });
    applyCustomState();
  }

  // Badge ("N selected") for one zone-group, recomputed from current
  // checkbox state.
  syncZoneGroupBadge(group) {
    const badge = group.querySelector("[data-zone-group-badge]");
    const checkedCount = group.querySelectorAll("input[data-zone-part]:checked").length;
    if (badge) {
      badge.textContent = checkedCount ? tformat(TBF_I18N.selectedCount, { count: checkedCount }) : "";
      badge.classList.toggle("is-hidden", !checkedCount);
    }
  }

  // The native file input's own chrome text ("Choose files"/"No file
  // chosen") follows the visitor's browser/OS language, not this page's -
  // it can't be restyled or relabeled with CSS. So the real input stays
  // hidden and a normal button + our own English status text drive it.
  setupFilePickers() {
    this.querySelectorAll("[data-file-picker]").forEach((wrap) => {
      const button = wrap.querySelector("button");
      const input = wrap.querySelector('input[type="file"]');
      const status = wrap.querySelector("[data-file-status]");
      if (!button || !input || !status) return;
      button.addEventListener("click", () => input.click());
      input.addEventListener("change", () => {
        const count = input.files.length;
        status.textContent = count === 0 ? TBF_I18N.filesNone : count === 1 ? TBF_I18N.filesOne : tformat(TBF_I18N.filesMany, { count });
      });
    });
  }

  // Custom country picker: filters the fixed country list as you type,
  // drops below the field, and dims the rest of the form while open - none
  // of which a native <datalist> popup lets a page control.
  setupAutocomplete() {
    this.querySelectorAll("[data-autocomplete]").forEach((wrap) => {
      const input = wrap.querySelector("[data-autocomplete-input]");
      const list = wrap.querySelector("[data-autocomplete-list]");
      if (!input || !list) return;
      const items = Array.from(list.querySelectorAll("li"));
      let overlay = null;

      const closeList = () => {
        list.classList.add("is-hidden");
        wrap.removeAttribute("data-open");
        if (overlay) {
          overlay.remove();
          overlay = null;
        }
      };

      const openList = () => {
        if (!overlay) {
          overlay = document.createElement("div");
          overlay.className = "bform-autocomplete-overlay";
          // Dawn's base.css hides any empty element outright (`div:empty {
          // display: none }`) - this overlay has no content by design
          // (just a click-catcher), which silently made it never render at
          // all: clicking "outside" the dropdown hit whatever was actually
          // underneath instead of this overlay, so the list never closed.
          // This was the real cause of the long-standing "dropdown dimming
          // does nothing on my phone" issue from earlier in the project.
          // An inline style beats the stylesheet rule regardless of
          // specificity.
          overlay.style.display = "block";
          overlay.addEventListener("click", closeList);
          // Appended directly to the <dialog> element itself, matching the
          // calendar overlay below - a shown <dialog> is promoted to the
          // browser's "top layer", so an overlay nested inside
          // .booking-form__dialog-inner (a plain non-positioned scroll
          // container) doesn't reliably paint above the rest of the dialog.
          this.dialogEl.appendChild(overlay);
        }
        wrap.setAttribute("data-open", "");
        list.classList.remove("is-hidden");
      };

      const filter = () => {
        const q = input.value.trim().toLowerCase();
        items.forEach((li) => {
          const match = q === "" || li.textContent.toLowerCase().includes(q);
          li.classList.toggle("is-hidden", !match);
        });
        // Typing something that matches nothing (e.g. non-English
        // characters) used to close the list - and with it, the dimming
        // overlay - the instant that happened, even though the field was
        // still focused and being typed into. Dimming should only ever go
        // away when the visitor actually leaves the field (picks a value,
        // clicks away), not just because the current text has zero
        // matches - so this always keeps the dropdown open while typing,
        // even with an empty result list.
        openList();
      };

      // Re-opening after already picking a value used to filter by that
      // same value (e.g. reopening the currency list right after picking
      // "EUR" only ever showed "EUR" again, with no way to switch to
      // another currency without first deleting the text by hand) - a
      // focus should always show the full list, with the field's text
      // selected so typing immediately narrows it back down. Only actual
      // typing should filter.
      input.addEventListener("focus", () => {
        items.forEach((li) => li.classList.remove("is-hidden"));
        openList();
        input.select();
      });
      input.addEventListener("input", filter);
      items.forEach((li) => {
        li.addEventListener("click", () => {
          input.value = li.textContent;
          closeList();
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      });
    });
  }

  // Custom calendar, replacing native <input type="date">: the native
  // picker's language, font and selection color all follow the visitor's
  // OS/browser and can't be styled from the page - same category of problem
  // as the country <datalist> above, same fix (build our own, dim the rest
  // while it's open).
  setupDatePickers() {
    const WEEKDAYS = TBF_I18N.weekdays;
    const MONTHS = TBF_I18N.months;
    const todayIso = new Date().toISOString().slice(0, 10);
    // Nobody realistically books a specific day more than a year out - past
    // that, "not sure yet" (a period option) is the honest answer, not a
    // firm date. Caps how far forward the picker will let anyone go.
    const maxDate = new Date();
    maxDate.setFullYear(maxDate.getFullYear() + 1);
    const maxIso = maxDate.toISOString().slice(0, 10);

    this.querySelectorAll("[data-datepicker]").forEach((wrap) => {
      const input = wrap.querySelector("input");
      if (!input) return;
      const isMulti = wrap.hasAttribute("data-date-multi");
      const role = wrap.dataset.dateRole; // "from" | "to" | undefined
      const pairId = wrap.dataset.datePair;

      let overlay = null;
      let panel = null;
      let viewYear, viewMonth; // 0-indexed month, the month currently shown
      let selected = new Set(input.value ? input.value.split(",").map((s) => s.trim()).filter(Boolean) : []);

      // For a linked From/To pair: "From" can't be picked after the current
      // "To" value, and "To" can't be picked before the current "From" -
      // read live each time the calendar opens, since the other field can
      // change between opens.
      const pairBound = () => {
        if (!pairId) return null;
        const pairInput = document.getElementById(pairId);
        return pairInput && pairInput.value ? pairInput.value : null;
      };

      const close = () => {
        if (panel) panel.remove();
        if (overlay) overlay.remove();
        panel = null;
        overlay = null;
        wrap.removeAttribute("data-open");
      };

      const commit = () => {
        input.value = Array.from(selected).sort().join(", ");
        input.dispatchEvent(new Event("change", { bubbles: true }));
      };

      const pick = (iso) => {
        if (isMulti) {
          if (selected.has(iso)) selected.delete(iso);
          else selected.add(iso);
          commit();
          render(); // stay open, let the visitor keep picking days
        } else {
          selected = new Set([iso]);
          commit();
          close();
        }
      };

      const render = () => {
        panel.innerHTML = "";
        const header = document.createElement("div");
        header.className = "bform-calendar__header";
        const prev = document.createElement("button");
        prev.type = "button";
        prev.textContent = TBF_I18N.calendarPrev;
        const label = document.createElement("span");
        label.textContent = `${MONTHS[viewMonth]} ${viewYear}`;
        const next = document.createElement("button");
        next.type = "button";
        next.textContent = TBF_I18N.calendarNext;
        prev.addEventListener("click", () => {
          viewMonth -= 1;
          if (viewMonth < 0) { viewMonth = 11; viewYear -= 1; }
          render();
        });
        next.addEventListener("click", () => {
          viewMonth += 1;
          if (viewMonth > 11) { viewMonth = 0; viewYear += 1; }
          render();
        });
        // Don't let the visitor page past the max-date month at all - every
        // day in a month beyond it would just render disabled anyway.
        const [maxY, maxM] = maxIso.split("-").map(Number);
        if (viewYear > maxY || (viewYear === maxY && viewMonth + 1 >= maxM)) {
          next.disabled = true;
        }
        header.append(prev, label, next);
        panel.appendChild(header);

        const grid = document.createElement("div");
        grid.className = "bform-calendar__grid";
        WEEKDAYS.forEach((d) => {
          const cell = document.createElement("span");
          cell.className = "bform-calendar__weekday";
          cell.textContent = d;
          grid.appendChild(cell);
        });

        const bound = pairBound();
        const firstOfMonth = new Date(viewYear, viewMonth, 1);
        const startOffset = firstOfMonth.getDay();
        const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
        for (let i = 0; i < startOffset; i++) grid.appendChild(document.createElement("span"));
        for (let d = 1; d <= daysInMonth; d++) {
          const iso = `${viewYear}-${String(viewMonth + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
          const cell = document.createElement("button");
          cell.type = "button";
          cell.className = "bform-calendar__day";
          cell.textContent = String(d);
          const beforeToday = iso < todayIso;
          const pastMaxDate = iso > maxIso;
          // A "from"/"to" range has to actually span more than one day - a
          // single specific day belongs in the separate "I know the exact
          // dates" -> "Specific day(s)" picker instead, so the same date
          // can't be picked for both ends here (<=/>=, not just </>).
          const beforeFrom = role === "to" && bound && iso <= bound;
          const afterTo = role === "from" && bound && iso >= bound;
          if (beforeToday || pastMaxDate || beforeFrom || afterTo) {
            cell.disabled = true;
          } else {
            if (selected.has(iso)) cell.classList.add("is-selected");
            cell.addEventListener("click", () => pick(iso));
          }
          grid.appendChild(cell);
        }
        panel.appendChild(grid);

        if (isMulti) {
          const done = document.createElement("button");
          done.type = "button";
          done.className = "bform-calendar__done";
          done.textContent = TBF_I18N.calendarDone;
          done.addEventListener("click", close);
          panel.appendChild(done);
        }
      };

      const open = () => {
        if (panel) return;
        const base = Array.from(selected)[0] || pairBound() || todayIso;
        const [y, m] = base.split("-").map(Number);
        viewYear = y;
        viewMonth = m - 1;

        overlay = document.createElement("div");
        overlay.className = "bform-autocomplete-overlay";
        // Same fix as the country/currency overlay above - Dawn's
        // `div:empty { display: none }` was silently hiding this
        // click-catcher entirely (it has no content by design).
        overlay.style.display = "block";
        overlay.addEventListener("click", close);
        // Appended directly to the <dialog> element itself (not a nested
        // descendant, not document.body): a shown <dialog> is promoted to
        // the browser's "top layer", rendered above all normal body
        // content regardless of z-index - appending to body put the
        // overlay *below* the dialog, invisible. The dialog's own box
        // (position:absolute + inset:0 below) is exactly the area that
        // needs dimming anyway.
        this.dialogEl.appendChild(overlay);

        panel = document.createElement("div");
        panel.className = "bform-calendar";
        wrap.appendChild(panel);
        wrap.setAttribute("data-open", "");
        render();
      };

      input.addEventListener("click", open);
      input.addEventListener("focus", open);
    });
  }

  // Layers every checked sub-part's overlay image on top of the base
  // silhouette (instead of drawing every possible zone combination by
  // hand) - one <img> per checked sub-part, absolutely stacked.
  updateZonePreview() {
    const preview = this.querySelector("[data-zone-preview-frame]");
    if (!preview) return;
    preview.querySelectorAll("[data-zone-preview-layer]").forEach((el) => el.remove());
    // Same "not listed" guard as updateSessionEstimate - its checkboxes can
    // stay checked (and :checked-matching) while hidden+disabled, so the
    // preview shouldn't keep showing their layers.
    const customArea = this.querySelector('input[name="body_parts_custom"]')?.checked;
    const checked = customArea
      ? []
      : Array.from(this.querySelectorAll("input[data-zone-part]:checked")).filter((b) => b.dataset.image);
    checked.forEach((box) => {
      // A part with the one/both switch has two possible overlays - which
      // side(s) actually got inked matters for what the diagram should
      // show, not just whether the part was picked at all. Falls back to
      // the single-side image if "both" is flipped on but no matching
      // both-sides overlay was ever uploaded for this part.
      const toggleInput = box.closest("[data-zone-part-row]")?.querySelector("[data-zone-part-toggle] input");
      const isBoth = toggleInput?.checked;
      const src = (isBoth && box.dataset.imageBoth) || box.dataset.image;
      const img = document.createElement("img");
      img.className = "zone-preview__layer";
      img.setAttribute("data-zone-preview-layer", "");
      img.alt = "";
      img.src = src;
      preview.appendChild(img);
    });
  }

  // A small bar pinned to the bottom of the dialog itself (not the page -
  // same "append straight to the <dialog>, position:absolute" technique as
  // the country/calendar dimming overlays, needed for the same top-layer
  // reason). This is now the ONLY place the estimate is shown - the
  // in-place readout stays in the DOM (hidden) purely as JS's data source,
  // so scrolling through a long constructor doesn't mean losing sight of
  // the number, and the form itself doesn't waste space repeating it.
  // Collapsed by default (a sleeve alone can be 4+ parts - the full
  // breakdown would eat a big chunk of a phone screen at all times) with a
  // chevron that flips on tap to reveal it, and the rest of the form dims
  // while expanded - same overlay technique as the country/calendar
  // dropdowns, so it reads unambiguously as "this opened something", not
  // just static text that happened to change.
  // The launch button is `position:fixed` so it's always reachable while
  // scrolling the page - but that also means it stays pinned in place once
  // the visitor scrolls all the way down, sitting on top of the footer's
  // links/policies instead of stopping above it. Switches it to
  // `position:absolute` (which, with no positioned ancestor, is anchored
  // to the document rather than the viewport - it scrolls normally) right
  // as the footer comes into view, docking it just above the footer
  // instead of covering it; switches back to fixed once the footer
  // scrolls back out of view.
  setupFooterDocking() {
    const btn = this.querySelector("[data-booking-open]");
    const footer = document.querySelector("footer.footer");
    if (!btn || !footer) return;

    const GAP = 24;

    // A blank spacer inserted right before the footer, sized to exactly
    // fit the docked button - otherwise "docking" just moved the overlap
    // problem earlier: the button still landed wherever the page's actual
    // content happened to end (e.g. right on top of the FAQ paragraph
    // above the footer), since nothing made room for it there.
    const spacer = document.createElement("div");
    spacer.setAttribute("aria-hidden", "true");
    // Dawn's base.css hides any empty <div> outright (`div:empty {
    // display: none }`), which this is by design (nothing goes inside it,
    // just a height) - an inline style beats that stylesheet rule
    // regardless of specificity, so it's forced back to visible.
    spacer.style.display = "block";
    footer.parentNode.insertBefore(spacer, footer);

    const dock = () => {
      spacer.style.height = `${btn.offsetHeight + GAP * 2}px`;
      const footerTop = footer.getBoundingClientRect().top + window.scrollY;
      btn.style.top = `${footerTop - btn.offsetHeight - GAP}px`;
      btn.classList.add("is-docked");
    };
    const undock = () => {
      btn.classList.remove("is-docked");
      btn.style.top = "";
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => (entry.isIntersecting ? dock() : undock()));
    });
    observer.observe(footer);

    window.addEventListener("resize", () => {
      if (btn.classList.contains("is-docked")) dock();
    });
  }

  setupFloatingEstimate() {
    const breakdown = this.querySelector("[data-session-estimate-breakdown]");
    if (!this.dialogInner) return;

    this.floatingEstimateEl = document.createElement("div");
    this.floatingEstimateEl.className = "booking-form__estimate-float is-hidden";

    const row = document.createElement("div");
    row.className = "booking-form__estimate-float__row";
    this.floatingEstimateText = document.createElement("span");
    this.floatingEstimateText.className = "booking-form__estimate-float__text";
    const chevron = document.createElement("span");
    chevron.className = "booking-form__estimate-float__chevron";
    chevron.setAttribute("aria-hidden", "true");
    chevron.textContent = "▾";
    row.append(this.floatingEstimateText, chevron);
    this.floatingEstimateEl.appendChild(row);

    // Short recap of the color/style choices made elsewhere on this step
    // (detail_level, tattoo_style) - lets a visitor glance at the bar and
    // confirm the whole picture, not just the session count.
    this.floatingEstimateSummary = document.createElement("div");
    this.floatingEstimateSummary.className = "booking-form__estimate-float__summary";
    this.floatingEstimateEl.appendChild(this.floatingEstimateSummary);

    if (breakdown) {
      // Re-parented, not copied - updateSessionEstimate() already fills
      // and shows/hides this exact element, no other code needs to change.
      this.floatingEstimateEl.appendChild(breakdown);
    }

    // All three notes live in one wrapper so their line-wrap width can be
    // pinned to match the breakdown grid's own (dynamic) rendered width
    // (see syncFloatingNotesWidth) - centering each note on the full bar
    // width independently made them wrap wider than the grid above them,
    // which read as mismatched/sloppy next to it.
    this.floatingNotesWrap = document.createElement("div");
    this.floatingNotesWrap.className = "booking-form__estimate-float__notes";

    // Each row is rounded on its own (see updateSessionEstimate) instead
    // of being forced to add up to the headline total - flagging that
    // directly so a visitor who does the addition themselves doesn't read
    // the mismatch as an error.
    this.roundingNoteEl = document.createElement("p");
    this.roundingNoteEl.className = "booking-form__estimate-float__note is-hidden";
    this.roundingNoteEl.textContent = TBF_I18N.roundingNote;
    this.floatingNotesWrap.appendChild(this.roundingNoteEl);

    // These two caveats used to sit as static text under the in-form
    // estimate - moved here (not duplicated) since the number itself
    // already lives only in the floating bar, so the caveats about that
    // number belong next to it instead of a spot further up the page.
    const note1 = document.createElement("p");
    note1.className = "booking-form__estimate-float__note";
    note1.textContent = TBF_I18N.notePain;
    const note2 = document.createElement("p");
    note2.className = "booking-form__estimate-float__note";
    note2.textContent = TBF_I18N.noteStepByStep;
    // Last in the list on purpose - a nuance for the (less common) case
    // where a design only partly bleeds into a picked area, not a caveat
    // that applies to every estimate the way the other two do.
    const note3 = document.createElement("p");
    note3.className = "booking-form__estimate-float__note";
    note3.textContent = TBF_I18N.notePartialCoverage;
    this.floatingNotesWrap.append(note1, note2, note3);
    this.floatingEstimateEl.appendChild(this.floatingNotesWrap);

    this.dialogEl.appendChild(this.floatingEstimateEl);

    // Dims the form by fading .booking-form__dialog-inner itself (an
    // element that's already there and already rendering correctly) rather
    // than creating a new overlay element and positioning it on top - the
    // dropdown/calendar overlays use that approach and, per Igor, still
    // show no dimming at all on his phone despite the CSS itself checking
    // out, so this deliberately avoids repeating whatever is actually
    // going wrong there until that gets diagnosed separately.
    const collapse = () => {
      this.floatingEstimateEl.classList.remove("is-expanded");
      this.dialogInner.classList.remove("is-dimmed");
      this.reserveFloatingEstimateSpace(true);
    };
    const expand = () => {
      this.floatingEstimateEl.classList.add("is-expanded");
      this.dialogInner.classList.add("is-dimmed");
      this.reserveFloatingEstimateSpace(true);
      this.syncFloatingNotesWidth();
    };
    row.addEventListener("click", () => {
      if (this.floatingEstimateEl.classList.contains("is-expanded")) collapse();
      else expand();
    });
    // dialogInner gets pointer-events:none while dimmed, so a tap anywhere
    // on the form "falls through" to the dialog itself instead of whatever
    // button/switch is underneath - nothing there gets accidentally
    // selected, and this collapses the bar back so the visitor can keep
    // filling the form with one more tap, not specifically the chevron.
    this.dialogEl.addEventListener("click", (e) => {
      if (this.floatingEstimateEl.classList.contains("is-expanded") && !this.floatingEstimateEl.contains(e.target)) {
        collapse();
      }
    });
    // The grid's own rendered width changes with viewport size (phone vs.
    // desktop) as much as with which parts are picked - re-sync on resize
    // too, not just whenever the breakdown re-renders.
    window.addEventListener("resize", () => this.syncFloatingNotesWidth());
    window.addEventListener("resize", () => this.syncHeaderHeightVar());
  }

  // Pins the notes' line-wrap width to the breakdown grid's own current
  // rendered width, so the caveats below it read as part of the same
  // block instead of independently centered across the full bar width.
  syncFloatingNotesWidth() {
    const breakdown = this.querySelector("[data-session-estimate-breakdown]");
    if (!breakdown || !this.floatingNotesWrap) return;
    const width = breakdown.offsetWidth;
    this.floatingNotesWrap.style.maxWidth = width ? `${width}px` : "";
  }

  applyFloatingEstimateVisibility() {
    if (!this.floatingEstimateEl) return;
    const show = this.currentStep === 2 && this.floatingEstimateText.textContent;
    this.floatingEstimateEl.classList.toggle("is-hidden", !show);
    this.reserveFloatingEstimateSpace(show);
  }

  // The bar is pinned to the bottom of the dialog regardless of scroll -
  // without reserving room for it, scrolling all the way down covered
  // Back/Next (and whatever else) right underneath it. Padding the
  // scrollable content itself (by the bar's actual current height - taller
  // once expanded) means the true end of the content clears the bar
  // instead of hiding behind it. Reading offsetHeight here forces the
  // layout to settle first, so it reflects the just-applied show/expand
  // state rather than whatever it was a frame ago.
  reserveFloatingEstimateSpace(show) {
    if (!this.dialogInner || !this.floatingEstimateEl) return;
    this.dialogInner.style.paddingBottom = show ? `${this.floatingEstimateEl.offsetHeight + 16}px` : "";
  }

  updateFloatingEstimate(text) {
    if (!this.floatingEstimateText) return;
    this.floatingEstimateText.textContent = text;
    this.applyFloatingEstimateVisibility();
  }

  // Short recap line in the bar: detail level + tattoo style, so a glance
  // at the bar confirms the whole picture, not just the session count.
  updateFloatingSummary() {
    if (!this.floatingEstimateSummary) return;
    const detailLabel = this.querySelector('input[name="detail_level"]:checked')?.closest("label")?.textContent?.trim();
    const styleLabel = this.querySelector('input[name="tattoo_style"]:checked')?.closest("label")?.textContent?.trim();
    const projectTypeLabels = Array.from(this.querySelectorAll('input[name="project_types"]:checked'))
      .map((box) => box.closest("label")?.textContent?.trim())
      .filter(Boolean);
    const parts = [detailLabel, styleLabel, ...projectTypeLabels].filter(Boolean);
    // Each item as its own inline nowrap span (not one plain text string) -
    // otherwise the browser could wrap mid-phrase (e.g. "Filling in
    // between existing tattoos" splitting across two lines). The " · "
    // separator is a separate, ordinary (breakable) text node BETWEEN
    // spans, not baked into a span's own nowrap text - a browser only
    // finds a line-break opportunity at actual whitespace, and putting the
    // separator's space inside a nowrap span suppressed it there too, so
    // the whole line ran off the edge with nowhere to wrap at all instead
    // of wrapping between items like intended.
    this.floatingEstimateSummary.replaceChildren();
    parts.forEach((part, i) => {
      const span = document.createElement("span");
      span.className = "booking-form__estimate-float__summary-item";
      span.textContent = part;
      this.floatingEstimateSummary.appendChild(span);
      if (i < parts.length - 1) {
        this.floatingEstimateSummary.appendChild(document.createTextNode(" · "));
      }
    });
    this.floatingEstimateSummary.classList.toggle("is-hidden", !parts.length);
    this.reserveFloatingEstimateSpace(!this.floatingEstimateEl.classList.contains("is-hidden"));
  }

  // Detail-level options share one preview image (not one picture per
  // option) since only one can ever be selected at a time - swap its src to
  // whichever option is currently checked instead.
  updateDetailPreview() {
    const img = this.querySelector("[data-detail-preview-image]");
    const placeholder = this.querySelector("[data-detail-preview-placeholder]");
    const caption = this.querySelector("[data-detail-preview-caption]");
    if (!img || !placeholder) return;
    const checked = this.querySelector('input[name="detail_level"]:checked');
    const src = checked?.dataset.image;
    if (src) {
      img.src = src;
      img.hidden = false;
      placeholder.hidden = true;
    } else {
      img.hidden = true;
      placeholder.hidden = false;
    }
    if (caption) {
      const description = checked?.dataset.description || "";
      caption.textContent = description;
      caption.hidden = !description;
    }
  }

  // Same pattern as updateDetailPreview() above, for the "What style are
  // you thinking?" options - a shared preview image swapped to whichever
  // style is currently checked, instead of one picture per option.
  updateStylePreview() {
    const img = this.querySelector("[data-style-preview-image]");
    const placeholder = this.querySelector("[data-style-preview-placeholder]");
    const caption = this.querySelector("[data-style-preview-caption]");
    if (!img || !placeholder) return;
    const checked = this.querySelector('input[name="tattoo_style"]:checked');
    const src = checked?.dataset.image;
    if (src) {
      img.src = src;
      img.hidden = false;
      placeholder.hidden = true;
    } else {
      img.hidden = true;
      placeholder.hidden = false;
    }
    if (caption) {
      const description = checked?.dataset.description || "";
      caption.textContent = description;
      caption.hidden = !description;
    }
  }

  // ---------------------------------------------------------------------
  // Session estimate: sub-part base ranges are summed per zone group (each
  // group has its own shared one/both toggle), then summed across every
  // checked group.
  // ---------------------------------------------------------------------

  updateSessionEstimate() {
    const out = this.querySelector("[data-session-estimate-output]");
    const breakdown = this.querySelector("[data-session-estimate-breakdown]");
    const hiddenInput = this.querySelector('input[name="session_estimate"][data-computed]');
    if (!out || !hiddenInput) return;

    const clearOutput = (message) => {
      out.textContent = message;
      if (breakdown) { breakdown.replaceChildren(); breakdown.classList.add("is-hidden"); }
      hiddenInput.value = "";
      this.updateFloatingEstimate("");
    };

    const customArea = this.querySelector('input[name="body_parts_custom"]')?.checked;
    if (customArea) {
      clearOutput(TBF_I18N.noCustomArea);
      return;
    }

    const checkedParts = Array.from(this.querySelectorAll("input[data-zone-part]:checked"));

    if (!checkedParts.length) {
      clearOutput(TBF_I18N.estimatePlaceholder);
      return;
    }

    // A detail level isn't required yet to show a running estimate - use
    // the standard "Black tattoo" multiplier (1×) as a stand-in the moment
    // the first area is picked, so the count/floating bar appears right
    // away instead of only after reaching the color/detail question later
    // on. It recalculates the instant an actual detail level is chosen.
    const detailEl = this.querySelector('input[name="detail_level"]:checked');
    const multiplier = detailEl ? Number(detailEl.dataset.multiplier || "1") : 1;

    // Each zone_part's base_min/base_max is a fixed number of sessions for
    // that specific area in the simplest style (solid black, no detail) -
    // e.g. 1 for a joint-sized zone like Elbow, 2 for a bigger flat area
    // like Chest, 3 for the biggest compound zone (Lower back & sides).
    // Doubled by its own one/both toggle where relevant. These sum first,
    // *then* the style multiplier is applied once to the total - not
    // per-part - so it matches how Igor actually estimates in practice
    // (a whole project's total time scales with style, not each piece of
    // it independently).
    const partEntries = []; // { label, base, baseSingle, factor, zoneGroup }
    let baseTotal = 0;
    checkedParts.forEach((box) => {
      const toggleInput = box.closest("[data-zone-part-row]")?.querySelector("[data-zone-part-toggle] input");
      const factor = toggleInput?.checked ? 2 : 1;
      const baseSingle = Number(box.dataset.baseMin || "0");
      const base = baseSingle * factor;
      partEntries.push({ label: box.dataset.partLabel || box.value, base, baseSingle, factor, zoneGroup: box.dataset.zoneGroupRef || "" });
      baseTotal += base;
    });

    const styledTotal = baseTotal * multiplier;
    // Real body size swings the actual number quite a bit around this
    // (Igor's own examples: a small client vs. a large, heavily-built one
    // can differ by several sessions on the same project) - shown as a
    // spread rather than pretending to a single exact figure.
    const VARIANCE = 0.05;
    // Always rounds up, never down - a session can't be booked as a
    // fraction, so "6.555 sessions worth of work" genuinely needs 7 real
    // session slots, not 6. Applies to the low end of the range too, not
    // just the high end (a floor() there would understate the minimum
    // actually required).
    let min = Math.max(1, Math.ceil(styledTotal * (1 - VARIANCE)));
    let max = Math.max(min, Math.ceil(styledTotal * (1 + VARIANCE)));
    const range = min === max ? `${min}` : `${min}–${max}`;
    const unit = min === max && min === 1 ? TBF_I18N.sessionSingular : TBF_I18N.sessionPlural;
    out.textContent = tformat(TBF_I18N.estimateLabel, { range });
    hiddenInput.value = range;
    this.updateFloatingEstimate(`${range} ${unit}`);

    if (breakdown) {
      breakdown.replaceChildren();
      if (partEntries.length > 1) {
        // Each row is judged entirely on its own, one formula for every
        // part regardless of whether it has a ×2 toggle - deliberately NOT
        // forced to sum to the headline min/max above. An earlier version
        // distributed the body-size spread across parts to make the rows
        // add up exactly, but that meant a part's displayed number
        // depended on what else was picked alongside it, not on its own
        // math - inflating small parts (e.g. a "1.10 sessions" chest
        // showing as a flat "2") just to balance the total. A per-part
        // estimate that doesn't quite add up to the total is normal for
        // rounded estimates and is called out in the note under the
        // breakdown - it's not a bug to hide.
        partEntries.forEach((p, i) => {
          const raw = p.baseSingle * multiplier;
          // Low end rounds down here (unlike the headline above) - this
          // row is informational context, not a slot being booked, so a
          // range like "1-2 sessions" honestly showing the uncertainty is
          // more useful than always inflating to the ceiling.
          const pMin = Math.max(1, Math.floor(raw * (1 - VARIANCE)));
          const pMax = Math.max(pMin, Math.ceil(raw * (1 + VARIANCE)));
          const pRange = pMin === pMax ? `${pMin}` : `${pMin}–${pMax}`;
          const pUnit = pMin === pMax && pMin === 1 ? TBF_I18N.sessionSingular : TBF_I18N.sessionPlural;
          // The sign only applies when:
          // 1. The displayed count is a single exact number, not a range -
          //    a "1-2 sessions" spread already communicates the same
          //    uncertainty on its own, no extra sign needed.
          // 2. That number's real value (base×style, BEFORE the ±5%
          //    body-size variance) is under 0.9, not just under 1 - the
          //    gap between 0.9 and 1 real sessions isn't a meaningfully
          //    different amount of work to flag.
          const isLessThanOne = pMin === pMax && pMin === 1 && raw < 0.9;
          const row = document.createElement("div");
          row.className = "booking-form__estimate-breakdown__row";
          // A little extra breathing room where the zone group changes
          // (Arm -> Leg, etc.) - not a visible divider, just enough gap to
          // read as "a new cluster starts here" without a line or a blank
          // row eating too much space.
          if (i > 0 && p.zoneGroup && p.zoneGroup !== partEntries[i - 1].zoneGroup) {
            row.classList.add("booking-form__estimate-breakdown__row--group-start");
          }
          const nameEl = document.createElement("span");
          nameEl.className = "booking-form__estimate-breakdown__name";
          nameEl.textContent = p.label;
          const signEl = document.createElement("span");
          signEl.className = "booking-form__estimate-breakdown__sign";
          signEl.textContent = isLessThanOne ? ">" : "";
          const countEl = document.createElement("span");
          countEl.className = "booking-form__estimate-breakdown__count";
          countEl.textContent = `${pRange} ${pUnit}`;
          const multiplierEl = document.createElement("span");
          multiplierEl.className = "booking-form__estimate-breakdown__multiplier";
          multiplierEl.textContent = p.factor === 2 ? "×2" : "";
          row.append(nameEl, signEl, countEl, multiplierEl);
          breakdown.appendChild(row);
        });
        breakdown.classList.remove("is-hidden");
        this.roundingNoteEl?.classList.remove("is-hidden");
      } else {
        breakdown.classList.add("is-hidden");
        this.roundingNoteEl?.classList.add("is-hidden");
      }
      this.syncFloatingNotesWidth?.();
    }
  }


  // ---------------------------------------------------------------------
  // sessionStorage persistence
  // ---------------------------------------------------------------------

  persistState() {
    try {
      const data = new FormData(this.formEl);
      const values = {};
      for (const [key, value] of data.entries()) {
        if (value instanceof File) continue; // files aren't restorable, skip
        if (values[key] === undefined) {
          values[key] = value;
        } else if (Array.isArray(values[key])) {
          values[key].push(value);
        } else {
          values[key] = [values[key], value];
        }
      }
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ step: this.currentStep, values }));
    } catch (e) {
      // sessionStorage can throw in locked-down browsing contexts - losing
      // resume-state isn't worth breaking the form over.
      console.warn("booking-form: could not persist state", e);
    }
  }

  restoreState() {
    let saved;
    try {
      saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "null");
    } catch (e) {
      return;
    }
    if (!saved) return;

    this.currentStep = saved.step || 1;
    Object.entries(saved.values || {}).forEach(([key, value]) => {
      const els = this.querySelectorAll(`[name="${CSS.escape(key)}"]`);
      if (!els.length) return;
      const values = Array.isArray(value) ? value : [value];
      els.forEach((el) => {
        if (el.type === "checkbox" || el.type === "radio") {
          el.checked = values.includes(el.value);
        } else {
          el.value = values[0];
        }
      });
    });
  }

  // ---------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------

  async handleSubmit(e) {
    e.preventDefault();
    if (!this.validateCurrentStep()) return;

    const workerUrl = this.dataset.workerUrl;
    const data = new FormData(this.formEl);

    const locationChoice = data.get("location_choice");
    const isHomeStudio = locationChoice === "home";
    const sessionEstimate = data.get("session_estimate");
    // Disabled fields are excluded from FormData automatically, but this
    // list is built by querying the DOM directly, so it needs its own
    // guard against stale `checked` state on a since-hidden "not listed"
    // custom-area branch (see setupCustomAreaExclusivity).
    const customArea = data.get("body_parts_custom") === "yes";
    const checkedParts = customArea
      ? []
      : Array.from(this.querySelectorAll("input[data-zone-part]:checked"));

    const payload = {
      honeypot: data.get("website") || "",
      name: data.get("name") || "",
      age_confirmed: data.get("age_confirmed") === "on",
      is_home_studio: isHomeStudio,
      location: isHomeStudio
        ? ""
        : [data.get("location_city"), data.get("location_country")].filter(Boolean).join(", "),
      // The "have you decided on the placement?" gate question was removed -
      // the area constructor is always shown now, so this is always true.
      chooses_placement: true,
      // Distinct from the (already-removed) "not sure where" case - this is
      // someone who knows exactly where they want it, it just isn't one of
      // the named areas below. Needs its own flag so the email doesn't read
      // like they have no preference at all.
      custom_area_requested: data.get("body_parts_custom") === "yes",
      // Each sub-part's own switch (zone_volume_<part id>) is never
      // included in body_parts on its own - fold its current side into the
      // label here so the actual choice (not just which sub-parts were
      // touched) reaches the notification email.
      body_parts: checkedParts.map((box) => {
        const toggle = box.closest("[data-zone-part-row]")?.querySelector("[data-zone-part-toggle]");
        const toggleInput = toggle?.querySelector("input");
        const toggleLabels = toggle?.querySelectorAll(".zone-part__toggle-label");
        const label = toggleInput && toggleLabels?.length === 2
          ? toggleLabels[toggleInput.checked ? 1 : 0].textContent.trim()
          : null;
        return label ? `${box.value} (${label})` : box.value;
      }),
      // Lets the worker flatten these into one composite picture for the
      // notification email instead of just a text list - same base +
      // one/both logic as updateZonePreview() above, just collected into
      // plain URLs instead of stacked <img> layers on the page itself.
      // Base first (bottom layer), so the worker can composite them in
      // array order without needing to guess which one is the base.
      body_part_images: customArea
        ? []
        : [this.querySelector("[data-zone-preview-base]")?.src, ...checkedParts.map((box) => {
            const toggleInput = box.closest("[data-zone-part-row]")?.querySelector("[data-zone-part-toggle] input");
            return (toggleInput?.checked && box.dataset.imageBoth) || box.dataset.image;
          })].filter(Boolean),
      detail_level: data.get("detail_level") || "",
      session_estimate: sessionEstimate || "",
      project_types: data.getAll("project_types"),
      tattoo_style: data.get("tattoo_style") || "",
      idea_description: data.get("idea_description") || "",
      // For "specific dates", this carries the raw comma-separated ISO
      // dates as-is (no English label baked in) - the worker reformats
      // them (European DD.MM.YYYY, one per line) for the notification
      // email. For "exact date range", it's just a copy of availability_from
      // (the worker prefers availability_from/to for display and ignores
      // this value in that case) - kept non-blank only so the "always
      // required" server-side check on this field still passes.
      availability_period:
        data.get("availability_choice") === "exact"
          ? data.get("availability_exact_mode") === "specific"
            ? data.get("availability_specific_dates") || ""
            : data.get("availability_from") || ""
          : data.get("availability_choice") || "",
      availability_from:
        data.get("availability_choice") === "exact" && data.get("availability_exact_mode") === "range"
          ? data.get("availability_from")
          : "",
      availability_to:
        data.get("availability_choice") === "exact" && data.get("availability_exact_mode") === "range"
          ? data.get("availability_to")
          : "",
      email: data.get("email") || "",
      instagram_handle: data.get("instagram_handle") || "",
      marketing_consent: data.get("marketing_consent") === "on",
    };

    const submitForm = new FormData();
    submitForm.append("payload", JSON.stringify(payload));
    this.formEl.querySelectorAll('input[type="file"][name="reference_images"]').forEach((input) => {
      Array.from(input.files || []).forEach((f) => submitForm.append("reference_images", f));
    });
    this.formEl.querySelectorAll('input[type="file"][name="placement_images"]').forEach((input) => {
      Array.from(input.files || []).forEach((f) => submitForm.append("placement_images", f));
    });

    this.setStatus(TBF_I18N.statusSending, "pending");
    if (this.submitButton) this.submitButton.disabled = true;

    try {
      const resp = await fetch(`${workerUrl}/submit`, { method: "POST", body: submitForm });
      const result = await resp.json().catch(() => ({ ok: false }));
      if (!resp.ok || !result.ok) {
        throw new Error(result.error || TBF_I18N.statusErrorGeneric);
      }
      sessionStorage.removeItem(STORAGE_KEY);
      this.setStatus(TBF_I18N.statusSuccess, "success");
      this.formEl.reset();
      window.setTimeout(() => {
        // Navigate straight to the redirect target instead of closing the
        // dialog first - closing it revealed the tattoos page underneath
        // for one visible frame before the navigation actually kicked in.
        // Skipping close() when we're about to leave the page entirely
        // avoids that flash; close() still runs on its own for the
        // no-redirect-configured fallback case.
        const redirectUrl = this.dataset.redirectUrl;
        if (redirectUrl) {
          // The redirect_url setting is a plain path (e.g. "/collections/all")
          // with no language prefix - navigating to it directly drops a
          // non-English visitor back into the shop's default language. Shopify
          // exposes the active locale as window.Shopify.locale; re-prepend it
          // if it's not already part of the path and isn't the default "en".
          const locale = window.Shopify && Shopify.locale && Shopify.locale !== "en" ? Shopify.locale : "";
          const alreadyLocalized = locale && (redirectUrl === `/${locale}` || redirectUrl.startsWith(`/${locale}/`));
          const finalUrl = locale && redirectUrl.startsWith("/") && !alreadyLocalized ? `/${locale}${redirectUrl}` : redirectUrl;
          window.location.href = finalUrl;
        } else {
          this.close();
        }
      }, 1800);
    } catch (err) {
      console.error("booking-form submit failed:", err);
      // Temporary: show the real error text instead of a generic message,
      // so a failure can actually be diagnosed instead of guessed at.
      this.setStatus(tformat(TBF_I18N.statusErrorPrefix, { message: err && err.message ? err.message : err }), "error");
    } finally {
      if (this.submitButton) this.submitButton.disabled = false;
    }
  }

  setStatus(message, kind) {
    if (!this.statusEl) return;
    this.statusEl.textContent = message;
    this.statusEl.dataset.kind = kind || "";
  }
}

customElements.define("booking-form", BookingForm);
